import { ProductType } from "../../common/interfaces/enums/ProductType";
import { SmokerStatus } from "../../common/interfaces/enums/SmokerStatus";
import IApiClient from "../interfaces/api/group/case/config/clients/IApiClient";
import { IApiGroupData } from "../interfaces/api/group/IApiGroupData";
import { IClient } from "../../common/interfaces/IClient";
import { IGroupResult } from "../interfaces/IGroupResult";
import { ApiResponse, ApiResponseMessage } from "../types/api/ApiResponses";
import { retrieveGroupMapper } from "./mapper/mapper";
import EnumMapper from "./mapper/EnumMapper";
import { IQuoteGroupResultData } from "../interfaces/IQuoteGroupResultData";
import { ApiGroupCaseResult } from "../types/api/ApiGroupCaseResult";
import IApiDiscount from "../interfaces/api/group/results/IApiDiscount";
import IBenefitDiscount from "../../common/interfaces/IBenefitDiscount";
import ResultMapper from "./mapper/ResultMapper";
import IGroupCase from "../interfaces/IGroupCase";

/**
 * Wrapper for fetch requests. Ensures typing is applied.
 * @param url Target url endpoint
 * @param arg any arguments for the request
 */
export function apiFetch<T extends ApiResponse<unknown>>(
    url: string,
    arg?: RequestInit
): Promise<T> {
    /**
     * We disable the next time, as the error handling should be handled downstream
     */

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    return fetch(url, arg).then((res) => {
        if (!res.ok) {
            throw new Error("Bad Response : " + res.statusText);
        }
        return res.json() as Promise<T>;
    });
}

export function validateMessageResponse(res: ApiResponseMessage<unknown>): void {
    if (!res.isSuccessful) {
        throw new Error(
            res.messages
                .filter((mes) => mes.type === "Error")
                .map((mes) => `${mes.type.toUpperCase()}: ${mes.text}`)
                .join("; ")
        );
    }
}

export const parseClient = (client: IApiClient): IClient => {
    return {
        age: client.age,
        isActive: true,
        isChild: client.isChild,
        gender: EnumMapper.Gender.remoteToLocal(client.gender),
        personId: client.personId,
        smokerStatus: client.isSmoker ? SmokerStatus.Smoker : SmokerStatus.NonSmoker
    };
};

export const parseGroupResult = (res: IApiGroupData): IGroupResult => {
    const resQuoteGroupCases: IGroupCase[] = [];
    const dataQuoteGroupCases = res.quoteGroupCases || [];

    if (dataQuoteGroupCases) {
        for (let i = 0; i < dataQuoteGroupCases.length; i++) {
            const config = dataQuoteGroupCases[i].quoteConfiguration;
            const productType = EnumMapper.Product.remoteToLocal(config.productType);
            const mapper = retrieveGroupMapper(productType);

            const setting = mapper.remoteToLocal(config);

            resQuoteGroupCases.push({
                isSelected: dataQuoteGroupCases[i].isSelected,
                config: setting
            });
        }
    }

    const clients = res.quoteGroupPersons.map(
        (c): IClient => ({
            personId: c.client.personId,
            age: c.client.age,
            gender: EnumMapper.Gender.remoteToLocal(c.client.gender),
            smokerStatus: c.client.isSmoker
                ? SmokerStatus.Smoker
                : SmokerStatus.NonSmoker,
            isChild: c.client.isChild,
            isActive: c.isActive
        })
    );

    return {
        groupId: res.groupGUID,
        clients: clients,
        hasContactDetails: res.hasContactDetails,
        quoteGroupCases: resQuoteGroupCases
    };
};

export const mapBenefitDiscount = (discount: IApiDiscount): IBenefitDiscount => {
    return {
        type: discount.type,
        value: discount.value,
        percentage: discount.percentage
    };
};

export const parseGroupResultData = (
    result: ApiGroupCaseResult
): IQuoteGroupResultData => {
    const product: ProductType = EnumMapper.Product.remoteToLocal(
        result.quoteConfiguration.productType
    );

    const mapper = retrieveGroupMapper(product);

    return {
        productId: result.productId,
        quoteResults: ResultMapper.remoteToLocal(result.quoteResult),
        quoteSetting: mapper.remoteToLocal(result.quoteConfiguration)
    };
};
